/**
 * 카드 삭제 확인 모달
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 */
import { AlertTriangle } from 'lucide-react';
import type { SavedCard } from '@/lib/api/types';

export interface DeleteCardConfirmModalProps {
  /** 삭제할 카드 */
  card: SavedCard | null;
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 삭제 중 여부 */
  isDeleting?: boolean;
  /** 모달 닫기 */
  onClose: () => void;
  /** 삭제 확인 */
  onConfirm: (cardId: number) => Promise<void>;
  /** 번역 텍스트 */
  t: {
    title: string;
    message: string;
    defaultCardWarning: string;
    cancel: string;
    delete: string;
    deleting: string;
  };
}

export function DeleteCardConfirmModal({
  card,
  isOpen,
  isDeleting = false,
  onClose,
  onConfirm,
  t,
}: DeleteCardConfirmModalProps) {
  if (!isOpen || !card) return null;

  const handleConfirm = async () => {
    await onConfirm(card.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* 모달 컨텐츠 */}
      <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-6 shadow-xl">
        {/* 경고 아이콘 */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>

        {/* 제목 */}
        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
          {t.title}
        </h3>

        {/* 카드 정보 */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg text-center">
          <p className="text-sm font-medium text-gray-700">
            {card.display_name}
          </p>
          <p className="text-xs text-gray-500">
            {card.masked_display} · {card.expiry_display}
          </p>
        </div>

        {/* 메시지 */}
        <p className="text-sm text-gray-600 text-center mb-4">
          {t.message}
        </p>

        {/* 기본 카드 경고 */}
        {card.is_default && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 text-center">
              {t.defaultCardWarning}
            </p>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            type="button"
            disabled={isDeleting}
          >
            {t.cancel}
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            disabled={isDeleting}
          >
            {isDeleting ? t.deleting : t.delete}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteCardConfirmModal;
